import { Link, useLocation, useParams } from 'react-router-dom';
import { downloadDocument, getDocument, rebuildDocument, resendDocument } from '../api/documents';
import { useAsyncData } from '../hooks/useAsyncData';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { JsonBlock } from '../components/JsonBlock';
import { StatusBadge } from '../components/StatusBadge';
import { CopyButton } from '../components/CopyButton';
import { RelatedLinks } from '../components/RelatedLinks';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { PageHeader } from '../components/FeatureStatus';
import { PayloadViewer } from '../components/PayloadViewer';
import { usePermission } from '../hooks/usePermission';
import { useState } from 'react';
import { describeDocumentDelivery } from '../lib/documentDelivery';
import { exchangesByCorrelationLink, executionsByCorrelationLink } from '../lib/crossLinks';

export function DocumentDetailPage() {
  const params = useParams<{ id: string }>();
  const { search } = useLocation();
  const documentId = params.id ?? '';
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const canRevealSensitive = usePermission('integration.payload.revealSensitive');
  const { data, isLoading, error, refresh, setData } = useAsyncData(
    () => getDocument(documentId),
    [documentId],
  );

  async function handleResend(reason = 'Manual logical resend') {
    const updated = await resendDocument(documentId, reason);
    setData(updated);
  }

  async function handleRebuild(reason = 'Rebuild from stored source data') {
    const updated = await rebuildDocument(documentId, reason);
    setData(updated);
  }

  async function handleDownload() {
    setDownloadError(null);
    try {
      const downloaded = await downloadDocument(documentId);
      const url = URL.createObjectURL(downloaded.blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = downloaded.filename ?? data?.filename ?? `${documentId}.txt`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setDownloadError(caught instanceof Error ? caught.message : 'Download failed.');
    }
  }

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!data) return <EmptyState label={`Document ${documentId} was not found.`} />;
  const delivery = describeDocumentDelivery(data);

  return (
    <div className="page-grid">
      <Link to={{ pathname: '/documents', search }} className="back-link">← Back to Messages</Link>
      <PageHeader
        eyebrow="Documents"
        title="Document detail"
        purpose="Inspect persisted generated content, validation history, source payload, and controlled document lifecycle actions."
        status="PRODUCTION_READY"
        connected
        next="Use resend or rebuild only when the impact summary matches the operational intent."
      />
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Document detail</h2>
            <p className="mono">{data.id}</p>
          </div>
          <div className="panel-actions">
            {canRevealSensitive ? (
              <button type="button" onClick={() => void handleDownload()} disabled={!data.body}>
                Download raw
              </button>
            ) : null}
            {downloadError ? <span className="form-error">{downloadError}</span> : null}
            <button type="button" onClick={() => setConfirm({
              title: 'Resend document',
              message: 'This records a logical resend request for the persisted document and updates its operational history.',
              confirmLabel: 'Resend',
              risk: 'medium',
              onConfirm: () => handleResend(),
            })}>
              Resend
            </button>
            {data.metadata?.['origin'] === 'MANUAL_RAW' ? null : (
              <button type="button" onClick={() => setConfirm({
                title: 'Rebuild document',
                message: 'This rebuilds generated output from the stored source payload. Existing history remains available.',
                confirmLabel: 'Rebuild',
                risk: 'high',
                requireText: 'REBUILD',
                onConfirm: () => handleRebuild(),
              })}>
                Rebuild
              </button>
            )}
          </div>
        </div>
        <div className="summary-grid">
          <div><strong>Type</strong><span>{data.documentType}</span></div>
          <div><strong>Status</strong><span><StatusBadge value={data.status} /></span></div>
          <div><strong>Delivery outcome</strong><span><StatusBadge value={delivery.label} /></span></div>
          <div><strong>Delivery reason</strong><span>{delivery.detail}</span></div>
          <div><strong>Destination</strong><span>{delivery.destination ?? 'n/a'}</span></div>
          <div><strong>Partner</strong><span>{data.partnerId ?? 'n/a'}</span></div>
          <div><strong>Reference</strong><span>{data.referenceId ?? 'n/a'}</span></div>
          <div>
            <strong>Correlation</strong>
            <span className="inline-copy">
              <span className="mono">{data.correlationId ?? 'n/a'}</span>
              {data.correlationId ? (
                <>
                  <CopyButton value={data.correlationId} label="Copy correlation ID" />
                  <RelatedLinks links={[
                    exchangesByCorrelationLink(data.correlationId, search),
                    executionsByCorrelationLink(data.correlationId, search),
                  ]}
                  />
                </>
              ) : null}
            </span>
          </div>
          <div><strong>Version</strong><span>{data.currentVersionNumber}</span></div>
          <div><strong>Filename</strong><span>{data.filename ?? 'n/a'}</span></div>
          <div><strong>Content type</strong><span>{data.contentType ?? 'n/a'}</span></div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Generated content viewer</h2>
            <p>{data.contentType ?? 'No content type available.'}</p>
          </div>
        </div>
        <PayloadViewer
          value={data.body || 'No generated body available.'}
          contentType={data.contentType}
          filename={data.filename ?? `${documentId}.txt`}
          className="code-block content-viewer raw-payload-viewer"
        />
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2>Validation</h2>
          <p>Errors and warnings from the latest stored validation result.</p>
        </div>
        <div className="two-column-lists">
          <div>
            <h3>Errors</h3>
            {data.validationResult.errors.length === 0 ? <p>No errors.</p> : (
              <ul className="plain-list">
                {data.validationResult.errors.map((item) => <li key={item}>{item}</li>)}
              </ul>
            )}
          </div>
          <div>
            <h3>Warnings</h3>
            {data.validationResult.warnings.length === 0 ? <p>No warnings.</p> : (
              <ul className="plain-list">
                {data.validationResult.warnings.map((item) => <li key={item}>{item}</li>)}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2>Attempts / history</h2>
          <p>Operational history for generate, validate, resend, and rebuild actions.</p>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Attempt</th>
              <th>Status</th>
              <th>Reason / outcome</th>
              <th>Version</th>
              <th>Trace</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {data.attempts.map((attempt) => (
              <tr key={attempt.id}>
                <td>{attempt.attemptType}</td>
                <td><StatusBadge value={attempt.status} /></td>
                <td>{attempt.reason ?? deliveryAttemptMessage(attempt.details) ?? 'n/a'}</td>
                <td>{attempt.documentVersionNumber ?? 'n/a'}</td>
                <td className="mono">{attempt.traceId ?? 'n/a'}</td>
                <td>{new Date(attempt.createdAt).toLocaleString('en-GB')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2>Source payload</h2>
        </div>
        <PayloadViewer value={data.sourcePayload} filename={`${data.id}.source-payload.json`} />
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2>Metadata</h2>
        </div>
        <JsonBlock value={data.metadata} />
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function deliveryAttemptMessage(details: Record<string, unknown>): string | undefined {
  const raw = details['delivery'];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const delivery = raw as Record<string, unknown>;
  return typeof delivery['message'] === 'string' ? delivery['message'] : undefined;
}
